import React, { useState, useRef, useEffect, useCallback } from 'react';
// REMOVE FaImage
import { FaCommentDots, FaSearch, FaPaperPlane, FaArrowLeft, FaPlus, FaCheck, FaTimes, FaPaperclip, FaFilePdf, FaFileWord, FaFile, FaEye, FaTrash, FaBan, FaUserSlash, FaEyeSlash, FaEllipsisV } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useAutoRefresh } from '../context/AppContext';
import { getMessages, sendMessage, sendMessageWithFile, markMessageRead, getChatRequests,  createChatRequest, updateChatRequest, getDoctors, adminGetConversations, getRegisteredPatients, deleteMessage, deleteConversation, sendTypingStatus, getTypingStatus, getFileUrl, blockConversation, disableConversation, unfollowConversation, MAX_ATTACHMENT_SIZE, MAX_ATTACHMENT_SIZE_MESSAGE } from '../services/api';

const convMenuItemStyle = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
  padding: '10px 14px', border: 'none', background: 'white', cursor: 'pointer',
  fontSize: 13, fontWeight: 600, color: 'var(--text-dark)',
};

const FilePreview = ({ msg }) => {
  if (!msg.file_url) return <span>{msg.content}</span>;
  const ext = msg.file_name?.split('.').pop()?.toLowerCase();
  const url = getFileUrl(msg.file_url);
  if (['jpg', 'jpeg', 'png'].includes(ext)) {
    return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={msg.file_name} style={{ maxWidth: 180, maxHeight: 180, borderRadius: 8, display: 'block' }} /></a>;
  }
  if (ext === 'pdf') return <a href={url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'inherit', textDecoration: 'none' }}><FaFilePdf color="#c62828" />{msg.file_name}</a>;
  if (['doc', 'docx'].includes(ext)) return <a href={url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'inherit', textDecoration: 'none' }}><FaFileWord color="#1565c0" />{msg.file_name}</a>;
  return <a href={url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'inherit', textDecoration: 'none' }}><FaFile />{msg.file_name || msg.content}</a>;
};

const Messages = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const [view, setView] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatRequests, setChatRequests] = useState([]);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [requestMsg, setRequestMsg] = useState('');
  // Doctor: new chat with patient
  const [patients, setPatients] = useState([]);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [newChatSaving, setNewChatSaving] = useState(false);
  // Admin: all conversations
  const [allConversations, setAllConversations] = useState([]);
  const [adminSelected, setAdminSelected] = useState(null);
  const [showConvMenu, setShowConvMenu] = useState(false);
  const [unfollowedContacts, setUnfollowedContacts] = useState([]);
  const [showUnfollowed, setShowUnfollowed] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchRequests = useCallback(async () => {
    try {
      if (role === 'Admin') {
        const res = await adminGetConversations();
        setAllConversations(res.data?.data || []);
        return;
      }
      const res = await getChatRequests();
      const reqs = res.data?.data || [];
      setChatRequests(reqs);
      let mapped = [];
      let unfollowed = [];
      if (role === 'Doctor') {
        const accepted = reqs.filter(r => r.status === 'accepted');
        mapped = accepted.filter(r => !r.doctorUnfollowed)
          .map(r => ({ id: r.patientId, name: r.patientName || 'Patient', requestId: r.id, raw: r }));
        unfollowed = accepted.filter(r => r.doctorUnfollowed)
          .map(r => ({ id: r.patientId, name: r.patientName || 'Patient', requestId: r.id, raw: r }));
      } else if (role === 'Patient') {
        const accepted = reqs.filter(r => r.status === 'accepted');
        mapped = accepted.filter(r => !r.patientUnfollowed)
          .map(r => ({ id: r.doctorId, name: r.doctorName || 'Doctor', requestId: r.id, raw: r }));
        unfollowed = accepted.filter(r => r.patientUnfollowed)
          .map(r => ({ id: r.doctorId, name: r.doctorName || 'Doctor', requestId: r.id, raw: r }));
      }
      setContacts(mapped);
      setUnfollowedContacts(unfollowed);
      // Keep the currently-open chat's block/disable state in sync with the latest fetch
      setSelected(prev => {
        if (!prev) return prev;
        const match = mapped.find(c => c.requestId === prev.requestId);
        return match ? { ...prev, raw: match.raw } : prev;
      });
    } catch {}
  }, [role]);

  const fetchDoctors = useCallback(async () => {
    if (role === 'Doctor') return;
    try {
      const res = await getDoctors();
      setDoctors(res.data?.data || []);
    } catch {}
  }, [role]);

  const fetchPatients = useCallback(async () => {
    if (role === 'Patient') return;
    try {
      const res = await getRegisteredPatients();
      setPatients(res.data?.data || []);
    } catch {}
  }, [role]);

  const doctorPhoto = (id) => getFileUrl(doctors.find(d => String(d.id) === String(id))?.profileImage) || null;
  const patientPhoto = (id) => getFileUrl(patients.find(p => String(p.userId || p.id) === String(id))?.account?.profileImage) || null;

  useEffect(() => { fetchRequests(); fetchDoctors(); fetchPatients(); }, [fetchRequests, fetchDoctors, fetchPatients]);
  useAutoRefresh(fetchRequests, 'all');

  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  const fetchActiveChatMessages = useCallback(async () => {
    if (view !== 'chat' || !selected) return;
    try {
      const otherId = String(selected.id);
      const myId = String(user.id);
      const [msgRes, typingRes] = await Promise.all([
        getMessages({ senderId: myId, receiverId: otherId }),
        getTypingStatus(otherId).catch(() => ({ data: { isTyping: false } }))
      ]);
      
      setIsTyping(typingRes.data?.isTyping || false);

      const msgs = msgRes.data?.data || [];
      // Only update state if length or last message changed to avoid unnecessary re-renders
      setMessages(prev => {
        if (prev.length !== msgs.length || (prev.length > 0 && prev[prev.length - 1].id !== msgs[msgs.length - 1].id)) {
          return msgs;
        }
        return prev;
      });
      // Also mark as read
      const unread = msgs.filter(m => m.receiverId === myId && !m.isRead);
      if (unread.length > 0) {
        unread.forEach(m => markMessageRead(m.id).catch(() => {}));
      }
    } catch {}
  }, [view, selected, user]);

  useAutoRefresh(fetchActiveChatMessages, 'all');

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const openChat = async (contact) => {
    setSelected(contact);
    setView('chat');
    setLoading(true);
    try {
      const otherId = String(contact.id);
      const myId = String(user.id);
      const res = await getMessages({ senderId: myId, receiverId: otherId });
      const msgs = res.data?.data || [];
      setMessages(msgs);
      msgs.filter(m => m.receiverId === myId && !m.isRead).forEach(m => markMessageRead(m.id).catch(() => {}));
    } catch { setMessages([]); }
    setLoading(false);
  };

  const openAdminChat = (conv) => {
    setAdminSelected(conv);
    setView('admin-chat');
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (!selected) return;
    
    // Throttle typing status to once every 2 seconds
    if (!typingTimeoutRef.current) {
      sendTypingStatus({ receiverId: String(selected.id) }).catch(() => {});
      typingTimeoutRef.current = setTimeout(() => {
        typingTimeoutRef.current = null;
      }, 2000);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !selected) return;
    if (isBlocked || isDisabled) { showToast('Messaging is currently blocked/disabled in this conversation.', 'error'); return; }
    const text = input.trim();
    setInput('');
    try {
      const res = await sendMessage({
        senderId: String(user.id),
        senderName: user.name,
        receiverId: String(selected.id),
        receiverName: selected.name,
        content: text,
        messageType: 'text',
      });
      setMessages(prev => [...prev, res.data?.data]);
    } catch (err) {
      showToast(err.message || 'Failed to send', 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selected) return;
    if (isBlocked || isDisabled) {
      showToast('Messaging is currently blocked/disabled in this conversation.', 'error');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      showToast(MAX_ATTACHMENT_SIZE_MESSAGE, 'error');
      e.target.value = '';
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('senderId', String(user.id));
      fd.append('senderName', user.name);
      fd.append('receiverId', String(selected.id));
      fd.append('receiverName', selected.name);
      fd.append('content', file.name);
      const res = await sendMessageWithFile(fd);
      setMessages(prev => [...prev, res.data?.data]);
      showToast('File sent!', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to upload file', 'error');
    }
    e.target.value = '';
  };

  const handleAcceptRequest = async (req) => {
    try {
      await updateChatRequest(req.id, { status: 'accepted' });
      showToast('Chat request accepted', 'success');
      fetchRequests();
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleRejectRequest = async (req) => {
    try {
      await updateChatRequest(req.id, { status: 'rejected' });
      showToast('Request rejected');
      fetchRequests();
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleSendRequest = async () => {
    if (!selectedDoctorId) return;
    try {
      const doc = doctors.find(d => d.id === parseInt(selectedDoctorId));
      await createChatRequest({ doctorId: selectedDoctorId, doctorName: doc?.name || 'Doctor', message: requestMsg });
      showToast('Chat request sent!', 'success');
      setShowRequestModal(false); setSelectedDoctorId(''); setRequestMsg('');
      fetchRequests();
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  // Doctor initiates a chat directly with a registered patient (auto-accepted)
  const handleStartDoctorChat = async () => {
    if (!selectedPatientId) return;
    setNewChatSaving(true);
    try {
      const patient = patients.find(p => String(p.userId || p.id) === selectedPatientId);
      const patId = patient?.userId || patient?.id;
      await createChatRequest({ patientId: String(patId), patientName: patient?.name || 'Patient' });
      showToast('Chat started!', 'success');
      setShowNewChatModal(false);
      setSelectedPatientId('');
      await fetchRequests();
      // Immediately open the chat
      openChat({ id: patId, name: patient?.name || 'Patient' });
    } catch (err) {
      // 409 = chat already exists — just open it
      if (err.response?.status === 409) {
        const patient = patients.find(p => String(p.userId || p.id) === selectedPatientId);
        const patId = patient?.userId || patient?.id;
        setShowNewChatModal(false);
        setSelectedPatientId('');
        await fetchRequests();
        openChat({ id: patId, name: patient?.name || 'Patient' });
      } else {
        showToast(err.message || 'Error', 'error');
      }
    }
    setNewChatSaving(false);
  };

  const handleDeleteMsg = async (msgId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await deleteMessage(msgId);
      setMessages(prev => prev.filter(m => m.id !== msgId));
      if (adminSelected) {
        setAdminSelected(prev => ({
          ...prev,
          messages: prev.messages.filter(m => m.id !== msgId)
        }));
      }
      showToast('Message deleted');
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleDeleteConversation = async (id) => {
    if (!window.confirm('Delete this entire conversation?')) return;
    try {
      await deleteConversation(id);
      setAllConversations(prev => prev.filter(c => c.id !== id));
      showToast('Conversation deleted');
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  // ── Block / Disable / Unfollow (Doctor & Patient conversation controls) ──────
  const myRoleKey = role.toLowerCase(); // 'doctor' | 'patient'
  const isBlocked  = selected?.raw?.blockedBy && selected.raw.blockedBy !== 'none';
  const iBlocked   = selected?.raw?.blockedBy === myRoleKey;
  const isDisabled = !!selected?.raw?.chatDisabled;
  const iDisabled  = selected?.raw?.disabledBy === myRoleKey;
  const canMessage = !!selected && !isBlocked && !isDisabled;

  const handleToggleBlock = async () => {
    if (!selected?.requestId) return;
    const willBlock = !isBlocked;
    if (willBlock && !window.confirm(`Block ${selected.name}? They will not be able to message you until you unblock them.`)) return;
    try {
      const res = await blockConversation(selected.requestId, willBlock ? 'block' : 'unblock');
      setSelected(prev => ({ ...prev, raw: res.data.data }));
      showToast(willBlock ? 'User blocked' : 'User unblocked', 'success');
      fetchRequests();
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleToggleDisable = async () => {
    if (!selected?.requestId) return;
    const willDisable = !isDisabled;
    if (willDisable && !window.confirm('Disable this chat? Neither of you will be able to send messages until it is re-enabled.')) return;
    try {
      const res = await disableConversation(selected.requestId, willDisable);
      setSelected(prev => ({ ...prev, raw: res.data.data }));
      showToast(willDisable ? 'Chat disabled' : 'Chat re-enabled', 'success');
      fetchRequests();
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  // Admin equivalents — act on adminSelected (fields are top-level, not under .raw)
  const handleAdminToggleBlock = async () => {
    if (!adminSelected?.id) return;
    const willBlock = adminSelected.blockedBy === 'none' || !adminSelected.blockedBy;
    if (willBlock && !window.confirm('Block messaging in this conversation?')) return;
    try {
      const res = await blockConversation(adminSelected.id, willBlock ? 'block' : 'unblock');
      setAdminSelected(prev => ({ ...prev, ...res.data.data }));
      showToast(willBlock ? 'Conversation blocked' : 'Conversation unblocked', 'success');
      fetchRequests();
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleAdminToggleDisable = async () => {
    if (!adminSelected?.id) return;
    const willDisable = !adminSelected.chatDisabled;
    if (willDisable && !window.confirm('Disable this chat?')) return;
    try {
      const res = await disableConversation(adminSelected.id, willDisable);
      setAdminSelected(prev => ({ ...prev, ...res.data.data }));
      showToast(willDisable ? 'Chat disabled' : 'Chat re-enabled', 'success');
      fetchRequests();
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleUnfollow = async () => {
    if (!selected?.requestId) return;
    try {
      await unfollowConversation(selected.requestId, true);
      showToast('Conversation unfollowed');
      setView('contacts');
      setSelected(null);
      fetchRequests();
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleRefollow = async (contact) => {
    try {
      await unfollowConversation(contact.requestId, false);
      showToast(`Following ${contact.name} again`, 'success');
      await fetchRequests();
      openChat({ id: contact.id, name: contact.name, requestId: contact.requestId, raw: contact.raw });
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const pendingRequests = chatRequests.filter(r => r.status === 'pending');
  const filteredContacts = contacts.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  // ── Admin all-conversations view ─────────────────────────────────────────────
  if (role === 'Admin') {
    if (view === 'admin-chat' && adminSelected) {
      return (
        <div className="page-wrapper">
          <Header title="Messages" />
          <Sidebar />
          <main className="page-content" style={{ padding: 0 }}>
            <div className="chat-view">
              <div className="chat-header" style={{ position: 'relative' }}>
                <button className="chat-back-btn" onClick={() => { setView('contacts'); setAdminSelected(null); setShowConvMenu(false); }}><FaArrowLeft /></button>
                <div className="chat-avatar">
                  {patientPhoto(adminSelected.patientId) ? (
                    <img src={patientPhoto(adminSelected.patientId)} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (adminSelected.patientName?.charAt(0) || 'P')}
                </div>
                <div className="chat-header-info">
                  <div className="chat-name">{adminSelected.patientName} ↔ Dr. {adminSelected.doctorName}</div>
                  <div className="chat-status" style={{ color: '#e65100' }}>Read-only · Admin view</div>
                </div>
                <button className="chat-back-btn" onClick={() => setShowConvMenu(v => !v)} title="Manage conversation"><FaEllipsisV /></button>
                {showConvMenu && (
                  <div style={{ position: 'absolute', top: '100%', right: 12, marginTop: 4, background: 'white', borderRadius: 10, boxShadow: 'var(--shadow)', zIndex: 20, overflow: 'hidden', minWidth: 190 }}>
                    <button onClick={() => { setShowConvMenu(false); handleAdminToggleDisable(); }} style={convMenuItemStyle}>
                      <FaBan /> {adminSelected.chatDisabled ? 'Enable Chat' : 'Disable Chat'}
                    </button>
                    <button onClick={() => { setShowConvMenu(false); handleAdminToggleBlock(); }} style={{ ...convMenuItemStyle, color: '#c62828' }}>
                      <FaUserSlash /> {(adminSelected.blockedBy && adminSelected.blockedBy !== 'none') ? 'Unblock conversation' : 'Block conversation'}
                    </button>
                  </div>
                )}
              </div>
              <div style={{ padding: '8px 16px', background: '#fff3e0', fontSize: 12, color: '#e65100', textAlign: 'center', flexShrink: 0 }}>
                You are viewing this conversation as an admin. You cannot send messages.
              </div>
              {(adminSelected.blockedBy && adminSelected.blockedBy !== 'none') || adminSelected.chatDisabled ? (
                <div style={{ padding: '8px 16px', background: '#ffebee', fontSize: 12, color: '#c62828', textAlign: 'center', flexShrink: 0 }}>
                  {(adminSelected.blockedBy && adminSelected.blockedBy !== 'none') ? `Messaging blocked (by ${adminSelected.blockedBy}).` : `Chat disabled (by ${adminSelected.disabledBy}).`}
                </div>
              ) : null}
              <div className="chat-messages">
                {(!adminSelected.messages || adminSelected.messages.length === 0) && (
                  <div className="empty-state"><FaCommentDots className="empty-state-icon" /><p>No messages yet</p></div>
                )}
                {(adminSelected.messages || []).map(msg => {
                  const isPatient = String(msg.senderId) === String(adminSelected.patientId);
                  return (
                    <div key={msg.id} className={`chat-bubble-wrap ${isPatient ? 'other' : 'me'}`}>
                      <div style={{ fontSize: 10, color: 'var(--text-light)', marginBottom: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{isPatient ? adminSelected.patientName : adminSelected.doctorName}</span>
                        <button onClick={() => handleDeleteMsg(msg.id)} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', padding: '2px 5px', fontSize: 11 }} title="Delete"><FaTrash /></button>
                      </div>
                      <div className={`chat-bubble ${isPatient ? 'bubble-other' : 'bubble-me'}`}>
                        <FilePreview msg={msg} />
                        <span className="bubble-time">{msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </main>
          <BottomNav />
          <style>{CHAT_STYLES}</style>
        </div>
      );
    }

    return (
      <div className="page-wrapper">
        <Header title="Messages" />
        <Sidebar />
        <main className="page-content">
          <div className="page-header">
            <div><h1><FaCommentDots style={{ color: 'var(--primary)', marginRight: 8 }} />All Conversations</h1><p>{allConversations.length} conversations</p></div>
          </div>
          <div className="search-bar"><FaSearch /><input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} /></div>
          {allConversations.length === 0 ? (
            <div className="empty-state"><FaCommentDots className="empty-state-icon" /><h3>No conversations yet</h3></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {allConversations
                .filter(c => (c.patientName + c.doctorName).toLowerCase().includes(search.toLowerCase()))
                .map(conv => (
                  <div key={conv.id} className="msg-contact-row card" onClick={() => openAdminChat(conv)}>
                    <div className="msg-avatar" style={{ background: 'linear-gradient(135deg,#E8500A,#FF6B2C)', color: 'white', overflow: 'hidden' }}>
                      {patientPhoto(conv.patientId) ? (
                        <img src={patientPhoto(conv.patientId)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (conv.patientName?.charAt(0) || 'P')}
                    </div>
                    <div className="msg-contact-info">
                      <div className="msg-contact-name">
                        {conv.patientName} ↔ Dr. {conv.doctorName}
                        {conv.blockedBy && conv.blockedBy !== 'none' && <span style={{ marginLeft: 8, fontSize: 10, background: '#ffebee', color: '#c62828', padding: '2px 6px', borderRadius: 6 }}>BLOCKED</span>}
                        {conv.chatDisabled && <span style={{ marginLeft: 8, fontSize: 10, background: '#fff3e0', color: '#e65100', padding: '2px 6px', borderRadius: 6 }}>DISABLED</span>}
                      </div>
                      <div className="msg-last-msg">
                        {conv.messages?.length > 0
                          ? (conv.messages[conv.messages.length - 1].content || 'File attachment')
                          : 'No messages yet'}
                      </div>
                    </div>
                    <div className="msg-meta" style={{ flexDirection: 'row', gap: 10 }}>
                      <button className="action-btn" style={{ background: '#ffebee', color: '#c62828', padding: '6px' }} onClick={(e) => { e.stopPropagation(); handleDeleteConversation(conv.id); }} title="Delete Entire Conversation">
                        <FaTrash />
                      </button>
                      <div className="msg-time" style={{ display: 'flex', alignItems: 'center' }}><FaEye style={{ marginRight: 4 }} /> View</div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </main>
        <BottomNav />
        <style>{CHAT_STYLES}</style>
      </div>
    );
  }

  // ── Doctor / Patient chat view ────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      <Header title="Messages" />
      <Sidebar />
      <main className="page-content" style={{ padding: view === 'chat' ? 0 : undefined }}>
        {view === 'chat' && selected ? (
          <div className="chat-view">
            <div className="chat-header" style={{ position: 'relative' }}>
              <button className="chat-back-btn" onClick={() => { setView('contacts'); setSelected(null); setShowConvMenu(false); }}><FaArrowLeft /></button>
              <div className="chat-avatar">
                {(role === 'Doctor' ? patientPhoto(selected.id) : doctorPhoto(selected.id)) ? (
                  <img src={role === 'Doctor' ? patientPhoto(selected.id) : doctorPhoto(selected.id)} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                ) : selected.name.charAt(0)}
              </div>
              <div className="chat-header-info">
                <div className="chat-name">{selected.name}</div>
                <div className="chat-status" style={{ color: (isBlocked || isDisabled) ? '#c62828' : '#2e7d32' }}>
                  {isBlocked ? (iBlocked ? 'Blocked by you' : 'Blocked') : isDisabled ? 'Chat disabled' : 'Active'}
                </div>
              </div>
              <button className="chat-back-btn" onClick={() => setShowConvMenu(v => !v)} title="Conversation options"><FaEllipsisV /></button>
              {showConvMenu && (
                <div style={{ position: 'absolute', top: '100%', right: 12, marginTop: 4, background: 'white', borderRadius: 10, boxShadow: 'var(--shadow)', zIndex: 20, overflow: 'hidden', minWidth: 190 }}>
                  <button onClick={() => { setShowConvMenu(false); handleUnfollow(); }} style={convMenuItemStyle}><FaEyeSlash /> Unfollow conversation</button>
                  {(!isDisabled || iDisabled || role === 'Admin') && (
                    <button onClick={() => { setShowConvMenu(false); handleToggleDisable(); }} style={convMenuItemStyle}>
                      <FaBan /> {isDisabled ? 'Enable Chat' : 'Disable Chat'}
                    </button>
                  )}
                  {(!isBlocked || iBlocked || role === 'Admin') && (
                    <button onClick={() => { setShowConvMenu(false); handleToggleBlock(); }} style={{ ...convMenuItemStyle, color: '#c62828' }}>
                      <FaUserSlash /> {isBlocked ? `Unblock ${selected.name}` : `Block ${selected.name}`}
                    </button>
                  )}
                </div>
              )}
            </div>
            {(isBlocked || isDisabled) && (
              <div style={{ padding: '8px 16px', background: '#ffebee', fontSize: 12, color: '#c62828', textAlign: 'center', flexShrink: 0 }}>
                {isBlocked
                  ? (iBlocked ? `You have blocked ${selected.name}. Unblock to resume messaging.` : `Messaging is blocked in this conversation.`)
                  : `This chat has been disabled${iDisabled ? ' by you' : ''}. ${iDisabled || role === 'Admin' ? 'Re-enable it to continue.' : 'Only the person who disabled it (or Admin) can re-enable it.'}`}
              </div>
            )}
            <div className="chat-messages">
              {loading && <div className="loading-container"><div className="spinner" /></div>}
              {!loading && messages.length === 0 && <div className="empty-state"><FaCommentDots className="empty-state-icon" /><p>No messages yet. Say hi!</p></div>}
              {messages.map(msg => {
                const isMe = String(msg.senderId) === String(user.id);
                return (
                  <div key={msg.id} className={`chat-bubble-wrap ${isMe ? 'me' : 'other'}`}>
                    <div className={`chat-bubble ${isMe ? 'bubble-me' : 'bubble-other'}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                        <FilePreview msg={msg} />
                        {(role === 'Admin' || role === 'Doctor') && (
                          <button onClick={() => handleDeleteMsg(msg.id)} style={{ background: 'none', border: 'none', color: isMe ? 'rgba(255,255,255,0.7)' : '#c62828', cursor: 'pointer', padding: 0, fontSize: 11, marginTop: 2 }} title="Delete"><FaTrash /></button>
                        )}
                      </div>
                      <span className="bubble-time">{msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </div>
                  </div>
                );
              })}
              {isTyping && (
                <div className="chat-bubble-wrap other" style={{ marginBottom: 8 }}>
                  <div className="chat-bubble bubble-other typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="chat-input-bar">
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                onChange={handleFileUpload}
              />
              <button className="attach-btn" onClick={() => fileInputRef.current?.click()} title="Attach file" disabled={!canMessage} style={!canMessage ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}><FaPaperclip /></button>
              <input
                value={input}
                onChange={handleInputChange}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                placeholder={canMessage ? 'Type a message...' : 'Messaging is unavailable in this conversation'}
                disabled={!canMessage}
              />
              <button className="send-btn" onClick={handleSend} disabled={!input.trim() || !canMessage}><FaPaperPlane /></button>
            </div>
          </div>
        ) : (
          <>
            <div className="page-header">
              <div>
                <h1><FaCommentDots style={{ color: 'var(--primary)', marginRight: 8 }} />Messages</h1>
                <p>{contacts.length} conversations</p>
              </div>
              {role === 'Patient' && (
                <button className="btn btn-primary" onClick={() => setShowRequestModal(true)}><FaPlus /> New Chat</button>
              )}
              {role === 'Doctor' && (
                <button className="btn btn-primary" onClick={() => setShowNewChatModal(true)}><FaPlus /> Message Patient</button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {['contacts', 'requests'].map(tab => (
                <button key={tab} onClick={() => setView(tab)} style={{ padding: '8px 16px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', background: view === tab ? 'var(--primary)' : 'white', color: view === tab ? 'white' : 'var(--text-medium)', boxShadow: 'var(--shadow)' }}>
                  {tab === 'contacts' ? 'Chats' : `Requests${pendingRequests.length > 0 ? ` (${pendingRequests.length})` : ''}`}
                </button>
              ))}
            </div>

            {view === 'contacts' ? (
              <>
                <div className="search-bar"><FaSearch /><input placeholder="Search conversations..." value={search} onChange={e => setSearch(e.target.value)} /></div>
                {filteredContacts.length === 0 ? (
                  <div className="empty-state"><FaCommentDots className="empty-state-icon" /><h3>No conversations yet</h3><p>{role === 'Patient' ? 'Send a chat request to a doctor to start messaging' : 'Accept a chat request to start messaging patients'}</p></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredContacts.map(c => (
                      <div key={c.id} className="msg-contact-row card" onClick={() => openChat(c)}>
                        <div className="msg-avatar" style={{ background: 'linear-gradient(135deg,#E8500A,#FF6B2C)', color: 'white', overflow: 'hidden' }}>
                          {(role === 'Doctor' ? patientPhoto(c.id) : doctorPhoto(c.id)) ? (
                            <img src={role === 'Doctor' ? patientPhoto(c.id) : doctorPhoto(c.id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : c.name.charAt(0)}
                        </div>
                        <div className="msg-contact-info">
                          <div className="msg-contact-name">{c.name}</div>
                          <div className="msg-last-msg">Tap to open chat</div>
                        </div>
                        <div className="msg-meta"><div className="msg-time">→</div></div>
                      </div>
                    ))}
                  </div>
                )}
                {unfollowedContacts.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <button onClick={() => setShowUnfollowed(v => !v)} style={{ background: 'none', border: 'none', color: 'var(--text-light)', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '4px 0' }}>
                      {showUnfollowed ? 'Hide' : 'Show'} unfollowed conversations ({unfollowedContacts.length})
                    </button>
                    {showUnfollowed && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: 0.7 }}>
                        {unfollowedContacts.map(c => (
                          <div key={c.id} className="msg-contact-row card">
                            <div className="msg-avatar" style={{ background: '#9e9e9e', color: 'white', overflow: 'hidden' }}>
                              {(role === 'Doctor' ? patientPhoto(c.id) : doctorPhoto(c.id)) ? (
                                <img src={role === 'Doctor' ? patientPhoto(c.id) : doctorPhoto(c.id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : c.name.charAt(0)}
                            </div>
                            <div className="msg-contact-info">
                              <div className="msg-contact-name">{c.name}</div>
                              <div className="msg-last-msg">Unfollowed — hidden from your chat list</div>
                            </div>
                            <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => handleRefollow(c)}>Follow again</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {chatRequests.length === 0 ? (
                  <div className="empty-state"><FaCommentDots className="empty-state-icon" /><h3>No chat requests</h3></div>
                ) : chatRequests.map(req => (
                  <div key={req.id} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, background: 'linear-gradient(135deg,#E8500A,#FF6B2C)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, flexShrink: 0, overflow: 'hidden' }}>
                      {(role === 'Doctor' ? patientPhoto(req.patientId) : doctorPhoto(req.doctorId)) ? (
                        <img src={role === 'Doctor' ? patientPhoto(req.patientId) : doctorPhoto(req.doctorId)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (role === 'Doctor' ? req.patientName : req.doctorName || '?').charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{role === 'Doctor' ? req.patientName : req.doctorName}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 2 }}>{req.message || 'Chat request'}</div>
                      <span className={`status-badge status-${req.status}`} style={{ marginTop: 4 }}>{req.status}</span>
                    </div>
                    {role === 'Doctor' && req.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="action-btn" style={{ background: '#e8f5e9', color: '#2e7d32' }} onClick={() => handleAcceptRequest(req)} title="Accept"><FaCheck /></button>
                        <button className="action-btn" style={{ background: '#ffebee', color: '#c62828' }} onClick={() => handleRejectRequest(req)} title="Reject"><FaTimes /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <BottomNav />

      {showRequestModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowRequestModal(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>New Chat Request</h2>
              <button className="modal-close" onClick={() => setShowRequestModal(false)}>×</button>
            </div>
            <div className="form-group">
              <label>Select Doctor *</label>
              <select value={selectedDoctorId} onChange={e => setSelectedDoctorId(e.target.value)}>
                <option value="">Choose a doctor...</option>
                {doctors.map(d => <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Message (optional)</label>
              <textarea value={requestMsg} onChange={e => setRequestMsg(e.target.value)} placeholder="Brief reason for consultation..." />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowRequestModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSendRequest} disabled={!selectedDoctorId}>Send Request</button>
            </div>
          </div>
        </div>
      )}

      {showNewChatModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowNewChatModal(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>Message a Patient</h2>
              <button className="modal-close" onClick={() => setShowNewChatModal(false)}>×</button>
            </div>
            <div className="form-group">
              <label>Select Patient *</label>
              {patients.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-light)' }}>No registered patients found.</p>
              ) : (
                <select value={selectedPatientId} onChange={e => setSelectedPatientId(e.target.value)}>
                  <option value="">Choose a patient...</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.userId || p.id}>{p.name}</option>
                  ))}
                </select>
              )}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 12 }}>
              The chat will open immediately. The patient will be notified and can reply directly.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowNewChatModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleStartDoctorChat} disabled={!selectedPatientId || newChatSaving}>
                {newChatSaving ? 'Opening...' : 'Start Chat'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{CHAT_STYLES}</style>
    </div>
  );
};

const CHAT_STYLES = `
  .msg-contact-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; cursor: pointer; margin-bottom: 4px; }
  .msg-avatar { width: 46px; height: 46px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 700; flex-shrink: 0; }
  .msg-contact-info { flex: 1; min-width: 0; }
  .msg-contact-name { font-size: 14px; font-weight: 700; }
  .msg-last-msg { font-size: 12px; color: var(--text-light); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px; }
  .msg-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
  .msg-time { font-size: 11px; color: var(--text-light); }
  .chat-view { display: flex; flex-direction: column; height: calc(100vh - var(--header-height) - var(--bottom-nav-height)); }
  .chat-header { display: flex; align-items: center; gap: 12px; background: white; padding: 12px 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); flex-shrink: 0; padding-top: calc(var(--header-height) + 12px); }
  .chat-back-btn { background: var(--primary-bg); border: none; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: var(--primary); cursor: pointer; flex-shrink: 0; }
  .chat-avatar { width: 42px; height: 42px; background: linear-gradient(135deg,#E8500A,#FF6B2C); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 17px; font-weight: 700; flex-shrink: 0; }
  .chat-header-info { flex: 1; }
  .chat-name { font-size: 15px; font-weight: 700; }
  .chat-status { font-size: 12px; color: #2e7d32; }
  .chat-messages { flex: 1; overflow-y: auto; padding: 16px; background: var(--primary-bg); display: flex; flex-direction: column; gap: 8px; }
  .chat-bubble-wrap { display: flex; flex-direction: column; }
  .chat-bubble-wrap.me { align-items: flex-end; }
  .chat-bubble { max-width: 75%; padding: 10px 14px; border-radius: 18px; font-size: 14px; }
  .bubble-me { background: linear-gradient(135deg,#E8500A,#FF6B2C); color: white; border-bottom-right-radius: 4px; }
  .bubble-other { background: white; color: var(--text-dark); border-bottom-left-radius: 4px; box-shadow: var(--shadow); }
  .bubble-time { display: block; font-size: 10px; margin-top: 4px; opacity: 0.7; text-align: right; }
  .typing-indicator { display: flex; gap: 4px; padding: 12px 16px; align-items: center; width: fit-content; }
  .typing-indicator span { width: 6px; height: 6px; background-color: var(--text-light); border-radius: 50%; animation: typing 1.4s infinite ease-in-out both; }
  .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
  .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
  @keyframes typing { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
  .chat-input-bar { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: white; box-shadow: 0 -2px 8px rgba(0,0,0,0.06); flex-shrink: 0; }
  .chat-input-bar input { flex: 1; border: 2px solid var(--border); border-radius: 24px; padding: 10px 16px; font-size: 14px; outline: none; }
  .chat-input-bar input:focus { border-color: var(--primary); }
  .attach-btn { width: 38px; height: 38px; background: var(--primary-bg); border: none; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: var(--primary); font-size: 16px; cursor: pointer; flex-shrink: 0; }
  .send-btn { width: 42px; height: 42px; background: linear-gradient(135deg,#E8500A,#FF6B2C); border: none; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; cursor: pointer; flex-shrink: 0; }
  .send-btn:disabled { opacity: 0.5; }
`;

export default Messages;
